/*
 * Some declarations below are deliberately unused: they belong to UI that is commented out in place
 * (the sign-in button). Deleting them would delete work in
 * progress, and whether that UI ships is a product decision rather than a lint one. Each one carries
 * its own `eslint-disable-next-line`, so the rule still catches anything new that goes unused here.
 */
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { FiUser, FiX } from 'react-icons/fi';
import styled from 'styled-components';
import BRUCE_LOGO_RED_PNG from '@/public/img/bruce-logo/bruce_logo_red.png';

import devices from '@/utilities/devices';

const Header = styled.header`
  display: block;
  align-items: center;
  position: fixed;
  top: 0px;
  width: 100%;
  z-index: 1000;
`;

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  width: 100%;
  z-index: 1000;
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: 0 0 40px -5px rgba(0, 0, 0, .08);
  padding: 1.1rem 1.5rem;
  transition: all .3s;
  img {
    display: block;
    width: 6rem;
    z-index: 1000;
    position: relative;
    transition: all .3s;
  }
   a {
     outline: 0;
   }

   @media screen and (${devices.mobile}) {
     img {
       width: 6em;
     }
     padding: .7rem 1.2rem;
   }
`;

const NavLinkContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  a {
    margin: 0 1.4em;
    color: ${({ theme }) => theme.color.greyDarker};
    text-decoration: none;
    outline: 0;
    transition: .3s;
    will-change: color;
    :focus {
      outline: 0;
    }
    :hover {
      color: ${({ theme }) => theme.color.black};
    }
  }

  .left-block {
    display: flex;
    padding-left: 1.5rem;
  }

  .right-block {
    display: flex;
    justify-content: space-around;
    padding-right: 1.5rem;

    > a {
      color: ${({ theme }) => theme.color.black};
    }
  }

  @media screen and (${devices.mobile}) {
    .left-block, .right-block {
      display: none;
    }
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    .left-block, .right-block {
      display: none;
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const SignInButton = styled.a`
  width: 9em;
  padding: .4em 0em .3em;
  margin-right: 1.5em;
  text-align: center;
  border: 2px solid ${props => props.isScrolled ? `${props.theme.color.prune}` : `${props.theme.color.white}`};
  border-radius: 16px;
  cursor: pointer;
  user-select: none;
  transition: .3s;
  :hover {
    background-color: ${props => props.isScrolled ? `${props.theme.color.prune}` : `${props.theme.color.white}`};
    color: ${props => props.isScrolled ? `${props.theme.color.white}` : `${props.theme.color.prune}`};
  }

  @media screen and (${devices.mobile}) {
    position: absolute;
    bottom: 1em;
    left: calc(50% - 4.5em);
    color: ${props => props.theme.color.white};
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    position: absolute;
    bottom: 1em;
    left: calc(50% - 4.5em);
    color: ${props => props.theme.color.white};
  }
`;

const BurgerButton = styled.div`
  display: none;
  position: relative;
  margin-right: 1em;
  padding: 0 .4em;
  cursor: pointer;
  z-index: 1000;
  div {
    transform: ${props => props.isOpen && 'translateX(10px)'};
    transition: .3s;
    :after {
      content: '';
      display: block;
      width: 25px;
      height: 2px;
      margin: 8px 0;
      background-color: ${({ theme }) => theme.color.black};
      transition: .3s;
    }

    :first-child:after {
      transform: ${props => props.isOpen ? 'rotate(45deg) translateY(14px) ' : 'rotate(0deg)'};
      background-color: ${props => props.isOpen && props.theme.color.black};
    }

    :nth-child(2):after {
      width: ${props => props.isOpen ? '0px' : '20px'};
      transform: translate(5px);
      background-color: ${props => props.isOpen && props.theme.color.black};
    }

    :last-child:after {
      transform: ${props => props.isOpen ? 'rotate(-45deg) translateY(-14px)' : 'rotate(0deg)'};
      background-color: ${props => props.isOpen && props.theme.color.black};
    }
  }
  @media screen and (${devices.mobile}) {
    display: block;
    padding-right: 1.3rem;
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    display: block;
    padding-right: 1.3rem;
  }
`;

const NavOverlay = styled.div`
  display: none;
  width: 100%;
  height: fill-available;
  min-height: 100vh;
  transform: translateX(${props => props.isOpen ? '0%' : '100%'});
  background-color: ${props => props.theme.color.white};
  position: absolute;
  top: 0;
  right: 0;
  z-index: 999;
  transition: all .3s;

  .nav-link-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    a {
      color: ${props => props.theme.color.black};
      text-decoration: none;
      font-size: 1.1em;
      padding: .7em 1em;
    }
  }

  @media screen and (${devices.mobile}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const Navbar = ({ isTriggerVisible, currentLang }) => {

	const { t } = useTranslation('navbar');
	const [showMobileNav, setShowMobileNav] = useState(false);
	const [showLangSwitch, setShowLangSwitch] = useState(true);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
	const [showSignIn, setShowSignIn] = useState(false);
	const [currentUserData, setCurrentUserData] = useState(null);
	const [isClient, setIsClient] = useState(false);

	const openNav = () => {
		setShowMobileNav(!showMobileNav);
	};

	useEffect(() => {
		// Set client-side flag to prevent hydration mismatch
		setIsClient(true);

		// Only access localStorage on client side
		if (typeof window !== 'undefined') {
			const userData = localStorage.getItem('Parse/1TFEtDWiBmayB3RotMzcVaFT5rVrl36SqdCPuakh/currentUser') ||
        localStorage.getItem('Parse/SHvI8l8Qp6T6cMjBuCDGedvl0mX1WjLS1O0uiHng/currentUser');

			if (userData) {
				try {
					setCurrentUserData(JSON.parse(userData));
				} catch (e) {
					console.error('Error parsing user data:', e);
				}
			}

			// show language switcher or not depending on current page
			window.location.href.includes('offre/') && setShowLangSwitch(false);
		}

		// show sign up button on specific page
		// window.location.href.includes('/offre-recrutement-reprise') || window.location.href.includes('/activity-restart-offer')  ? (
		//   this.setState({showSignIn: true}),
		// ) : (
		//   this.setState({showSignIn: false})
		// )
	}, []);

	const NavbarLinks = t('navbar', { returnObjects: true }) ?? [];
	const MobileNavbarLinks = t('mobile', { returnObjects: true }) ?? [];

	return (
		<Header>
			<NavbarContainer isScrolled={isTriggerVisible} isOpen={showMobileNav}>
				<a className="bruce-logo" href={t('logo.href')}>
					<img
						alt='bruce logo'
						src={BRUCE_LOGO_RED_PNG.src}
					/>
				</a>
				<NavLinkContainer isScrolled={isTriggerVisible}>
					<div className="left-block">
						{Array.isArray(NavbarLinks) && NavbarLinks.map((el, key) => {
							return (
								<a key={key} href={el.href} name={el.name} id={el.gtm_id}> {el.name} </a>
							);
						})}
					</div>
					<div className="right-block">
						{/* sign up button for freemium page */}
						{/* {(showSignIn && currentUser === null)
							&& <SignInButton id={t('subscribeBtn.gtm_id')} href={t('subscribeBtn.href')} isScrolled={isScrolled}>
								{t('subscribeBtn.name')}
							</SignInButton>} */}
						{isClient && showLangSwitch && <LangSwitch language={currentLang} />}
						{isClient && currentUserData !== null && <ProfileButton isScrolled={isTriggerVisible} currentUserData={currentUserData} />}
					</div>
				</NavLinkContainer>
				<BurgerButton
					isScrolled={isTriggerVisible}
					isOpen={showMobileNav}
					onClick={() => openNav()}>
					<div />
					<div />
					<div />
				</BurgerButton>
				<NavOverlay isOpen={showMobileNav}>
					<div className="nav-link-container">
						{Array.isArray(MobileNavbarLinks) && MobileNavbarLinks.map((el, key) => {
							return (
								<a key={key} href={el.href} name={el.name} id={el.gtm_id}> {el.name} </a>
							);
						})}
						{(showSignIn && !isClient) && <a id={t('subscribeBtn.gtm_id')} href={t('subscribeBtn.href')} name={t('subscribeBtn.name')}> {t('subscribeBtn.name')} </a>}
					</div>
					{isClient && showLangSwitch && <LangSwitch language={currentLang} />}
				</NavOverlay>
			</NavbarContainer>
		</Header>
	);
};

Navbar.propTypes = {
	isTriggerVisible: PropTypes.bool,
};

export default Navbar;

/* LANGSWITCH COMPONENT */
const LangPickerContainer = styled.a`
  width: 8em;
  padding: .45em 0em .3em;
  font-size: .9rem;
  text-align: center;
  border: 2px solid ${props => props.theme.color.prune};
  border-radius: 40px;
  background-color: ${props => props.active && props.theme.color.prune};
  color: ${({ theme, active }) => active && theme.color.white} !important;
  cursor: pointer;
  user-select: none;
  transition: .3s;
  :hover {
    background-color: ${props => props.theme.color.prune};
    color: ${({ theme }) => theme.color.white}!important;
  }

  @media screen and (${devices.mobile}) {
    position: absolute;
    bottom: 1em;
    left: calc(50% - 4.5em);
    color: ${props => props.theme.color.black};
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    position: absolute;
    bottom: 1em;
    left: calc(50% - 4.5em);
    color: ${props => props.theme.color.black};
  }
`;

const PopupContainer = styled.div`
  position: absolute;
  width: 8.5em;
  background-color: ${props => props.theme.color.white};
  color: ${props => props.theme.color.black};
  border: 2px solid #ddd;
  border-radius: 16px;
  margin-top: 1em;
  margin-left: -.2em;
  font-weight: 400;
  transition: all ease .4s;
  animation: ease ${props => props.show ? 'show' : 'hide'} .25s;

  @keyframes show {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
}

@keyframes hide {
  0% {
    transform: translateY(0px);
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(8px);
  }
}

@media screen and (${devices.mobile}) {
  transform: translateY(-125px);
  margin-top: 0em;
  @keyframes show {
    0% {
      opacity: 0;
      transform: translateY(-118px);
    }
    100% {
      opacity: 1;
      transform: translateY(-125px);
    }
  }

  @keyframes hide {
    0% {
      transform: translateY(-125px);
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translateY(-118px);
    }
  }
}
@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
  transform: translateY(-125px);
  margin-top: 0em;
  @keyframes show {
    0% {
      opacity: 0;
      transform: translateY(-118px);
    }
    100% {
      opacity: 1;
      transform: translateY(-125px);
    }
  }

  @keyframes hide {
    0% {
      transform: translateY(-125px);
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translateY(-118px);
    }
  }
}
`;

const Lang = styled.a`
  display: block;
  padding: .5em .4em;
  cursor: pointer;
  transition: .3s;
  color: ${({ theme }) => theme.color.black} !important;
  margin: 0 !important;

  :first-child {
    border-radius: 10px 10px 0 0;
  }

  :last-child {
    border-radius: 0 0 10px 10px;
  }

  :hover {
    background-color: #f4f4f4;
  }
`;

const LangSwitch = ({ language }) => {
	const [showPicker, setShowPicker] = useState(false);
	const [shouldRender, setRender] = useState(showPicker);

	useEffect(() => {
		if (showPicker) {
			setRender(true);
		}
	}, [showPicker]);

	const onAnimationEnd = () => {
		if (!showPicker) {
			setRender(false);
		}
	};

	const CurrentLang = (currentLang) => {
		switch (currentLang) {
			case 'fr':
				return 'Français';
			case 'en':
				return 'English';
			case 'es':
				return 'Español';
			default:
				'Anglais';
		}
	};

	return (
		<LangPickerContainer active={showPicker} onClick={() => setShowPicker((isPickerShown) => !isPickerShown)}>
			<div>{CurrentLang(language)}</div>
			{shouldRender && (
				<PopupContainer
					show={showPicker}
					onAnimationEnd={onAnimationEnd}
				>
					{language === 'fr' && (
						<>
							<Lang href='/en'> Anglais </Lang>
							<Lang href='/es'> Espagnol </Lang>
						</>
					)}
					{language === 'en' && (
						<>
							<Lang href='/fr'> French </Lang>
							<Lang href='/es'> Spanish </Lang>
						</>
					)}
					{language === 'es' && (
						<>
							<Lang href='/fr'> Francés </Lang>
							<Lang href='/en'> Inglés </Lang>
						</>
					)}
				</PopupContainer>
			)}
		</LangPickerContainer>
	);
};

LangSwitch.propTypes = {
	language: PropTypes.string,
};
/* --- */

/* NAV ALERT COMPONENT */
const Alert = styled.div`
  display: ${props => props.isShowed === 'show' ? 'flex' : 'none'};
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${props => props.theme.color.prune};
  padding: .5em 0 .6em;
  color: ${props => props.theme.color.white};
  .text-container {
    width: 60%;
    font-size: .93em;
  }

  @media screen and (${devices.mobile}) {
    padding: 2em 0em .5em;
    .text-container {
      width: 98%;
      font-size: .78em;
    }
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    .text-container {
      width: 96%;
    }
  }
  svg {
    position: absolute;
    top: 5px;
    right: 5px;
    font-size: 1.4em;
    padding: .2em;
    cursor: pointer;
    float: right;
    transition: .2s;
    :hover {
      transform: scale(1.3);
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const NavAlert = ({ children }) => {
	const [showAlertBanner, setShowAlertBanner] = useState('hide'); // Default to hide to prevent hydration mismatch
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		if (typeof window !== 'undefined' && sessionStorage) {
			const savedState = sessionStorage.getItem('alertBanner');
			setShowAlertBanner(savedState || 'show');
		}
	}, []);

	const closeBanner = () => {
		if (typeof window !== 'undefined' && sessionStorage) {
			sessionStorage.setItem('alertBanner', 'hide');
		}
		setShowAlertBanner('hide');
	};

	if (!isClient) {
		return null; // Don't render on server side to prevent hydration mismatch
	}

	return (
		<Alert isShowed={showAlertBanner}>
			<div className="text-container">
				{children}
			</div>
			<FiX onClick={() => closeBanner()} />
		</Alert>
	);
};
/* --- */

/* PROFILE BUTTON COMPONENT */
const ProfileButtonContainer = styled.a`
  position: relative;
  display: flex;
  justify-content: space-between;
  width: auto;
  text-align: center;
  border-radius: 18px 16px 16px 18px;
  cursor: pointer;
  user-select: none;
  transition: .3s;

  .profil-pic {
    background-image: url(${props => props.avatar});
    background-size:cover;
    background-position: center;
    background-repeat: no-repeat;
    width: 42px;
    height: auto;
    border-radius: 14px 0px 0px 14px;
  }

  svg {
    align-self: center;
    width: 20px;
    height: 42px;
    padding: 0 .77em;
    background-color: ${({ theme }) => theme.color.prune};
    color: ${({ theme }) => theme.color.white};
    border-radius: 16px 0 0 16px;
    transition: .3s;
  }

  .name {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0em 1.2em 0em 1em;
    font-size: .84em;
    border: 1px solid ${({ theme }) => theme.color.greyDarker};
    border-left: 0;
    border-radius: 0px 16px 16px 0px;
    transition: border .3s;
    > span {
      ::first-letter {
        text-transform: capitalize;
      }
      :first-child {
        padding-right: .4em;
      }
    }
  }

  :hover {
    background-color: ${props => props.isScrolled ? `${props.theme.color.prune}` : `${props.theme.color.white}`};
    color: ${props => props.isScrolled ? `${props.theme.color.white}` : `${props.theme.color.prune}`};
  }
`;

const ProfileButton = ({ isScrolled, currentUserData }) => {
	const [currentUser, setCurrentUser] = useState(null);
	const [currentOriginUrl, setCurrentOriginUrl] = useState('');
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);

		if (currentUserData !== null) {
			setCurrentUser({
				firstName: currentUserData.firstName && currentUserData.firstName,
				lastName: currentUserData.lastName && currentUserData.lastName,
				role: currentUserData.role,
				avatarUrl: currentUserData.avatar && currentUserData.avatar.url,
			});
		} else {
			setCurrentUser(currentUserData);
		}

		if (typeof window !== 'undefined' && window.location) {
			setCurrentOriginUrl(window.location.origin);
		}
	}, [currentUserData]);

	if (!isClient) {
		return null; // Don't render on server side to prevent hydration mismatch
	}

	return (
		<>
			{(currentUser !== null && currentUser.role !== 'worker') && (
				<ProfileButtonContainer
					isScrolled={isScrolled}
					href={`${currentOriginUrl}/app`}
					avatar={currentUser.avatarUrl}
				>
					<>
						{currentUser?.avatarUrl !== undefined ? (
							<div className="profil-pic" />
						) : (
							<FiUser />
						)}
						<span className="name">
							<span>{currentUser.firstName}</span><span>{currentUser.lastName}</span>
						</span>
					</>
				</ProfileButtonContainer>
			)}
		</>
	);
};
